/* eslint-disable max-lines-per-function */
import { useState, useCallback, useContext } from "react";
import { VirtualBox, PackageConfigState } from "../interfaces/packageConfig";
import { GET, PUT } from "../utils/constants";
import { fetchData } from "../utils/dataProvider";
import { UserContext } from "../contexts/userContext";

export const usePackageConfig = () => {
  const { currentUser } = useContext(UserContext);

  const initialConfig = {
    user_cuit: currentUser.entityInfo.cuit,
    packaging_weight: 0,
    rules: {
      max_units_per_parcel: null,
      max_weight_per_parcel: null,
      max_volume_per_parcel: null,
    },
    virtual_boxes: [],
  };

  const [state, setState] = useState<PackageConfigState>({
    config: initialConfig,
    modalOpen: false,
    editingBox: null,
    formErrors: {},
    saveConfigError: null,
    hasUnsavedChanges: false,
    loading: false,
  });

  const getPackageConfiguration = async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const fetchedPackageConfiguration = await fetchData({
        url: "/packaging_config",
        method: GET,
      });

      setState((prev) => ({
        ...prev,
        config: fetchedPackageConfiguration,
        hasUnsavedChanges: false,
        loading: false,
      }));
    } catch (error) {
      console.error("Error loading packaging configuration:", error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const openModal = useCallback((virtualBox?: VirtualBox) => {
    setState((prev) => ({
      ...prev,
      modalOpen: true,
      editingBox: virtualBox || null,
      hasUnsavedChanges: true,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      modalOpen: false,
      editingBox: null,
    }));
  }, []);

  const saveVirtualBox = useCallback((virtualBox: VirtualBox) => {
    setState((prev) => {
      const restErrors = { ...prev.formErrors };
      delete restErrors.virtualBoxes;

      const existingBoxIndex = prev.config.virtual_boxes.findIndex(
        (box) => box._id === virtualBox._id,
      );

      let updatedBoxes: VirtualBox[];

      if (existingBoxIndex >= 0) {
        updatedBoxes = prev.config.virtual_boxes.map((box) =>
          box._id === virtualBox._id ? virtualBox : box,
        );
      } else {
        updatedBoxes = [
          ...prev.config.virtual_boxes,
          {
            ...virtualBox,
            _id: virtualBox._id || `box_${Date.now()}`,
            active: virtualBox.active ?? true,
          },
        ];
      }

      const maxWeight = Math.max(
        ...updatedBoxes.map((box) => box.max_weight),
        0,
      );

      return {
        ...prev,
        config: {
          ...prev.config,
          virtual_boxes: updatedBoxes,
          rules: {
            ...prev.config.rules,
            max_weight_per_parcel: maxWeight,
          },
        },
        formErrors: restErrors,
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const deleteVirtualBox = useCallback((boxId: string) => {
    setState((prev) => {
      const updatedBoxes = prev.config.virtual_boxes.filter(
        (box) => box._id !== boxId,
      );

      const maxWeight =
        updatedBoxes.length > 0
          ? Math.max(...updatedBoxes.map((box) => box.max_weight))
          : null;

      return {
        ...prev,
        config: {
          ...prev.config,
          virtual_boxes: updatedBoxes,
          rules: {
            ...prev.config.rules,
            max_weight_per_parcel: maxWeight,
          },
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const updateMaxUnitsPerParcel = useCallback((maxUnits: number) => {
    setState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        rules: {
          ...prev.config.rules,
          max_units_per_parcel: maxUnits === 0 ? null : maxUnits,
        },
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  const updateMaxWeightPerParcel = useCallback((weight: number) => {
    setState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        rules: {
          ...prev.config.rules,
          max_weight_per_parcel: weight === 0 ? null : weight,
        },
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  const updateMaxVolumePerParcel = useCallback((volume: number) => {
    setState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        rules: {
          ...prev.config.rules,
          max_volume_per_parcel: volume === 0 ? null : volume,
        },
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  const validatePackagingConfiguration = (): boolean => {
    const errors: Record<string, string> = {};

    if (state.config.virtual_boxes.length === 0) {
      errors.virtualBoxes = "Debe agregar al menos una caja virtual";
    }

    setState((prev) => ({
      ...prev,
      formErrors: errors,
    }));

    return Object.keys(errors).length === 0;
  };

  const saveConfiguration = useCallback(async (): Promise<void> => {
    try {
      setState((prev) => ({
        ...prev,
        loading: true,
      }));
      validatePackagingConfiguration();
      const payload = {
        user_cuit: state.config.user_cuit,
        packaging_weight: state.config.packaging_weight,
        rules: state.config.rules,
        virtual_boxes: state.config.virtual_boxes.map(({ _id, ...box }) => box),
      };

      await fetchData({
        url: "/packaging_config",
        method: PUT,
        body: payload,
      });

      setState((prev) => ({
        ...prev,
        hasUnsavedChanges: false,
        saveConfigError: null,
        formErrors: {},
        loading: false,
      }));
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        hasUnsavedChanges: false,
        loading: false,
        saveConfigError: error?.message
          ? error.message
          : "Ha ocurrido un error al guardar la configuración de paquetes. Intentelo mas tarde.",
      }));
    }
  }, [state.config, validatePackagingConfiguration]);

  const toggleVirtualBox = (boxId: string, active: boolean) => {
    setState((prevState) => ({
      ...prevState,
      config: {
        ...prevState.config,
        virtual_boxes: prevState.config.virtual_boxes.map((box) =>
          box._id === boxId ? { ...box, active } : box,
        ),
      },
      hasUnsavedChanges: true,
    }));
  };
  const resetConfiguration = useCallback(() => {
    setState({
      config: initialConfig,
      modalOpen: false,
      editingBox: null,
      formErrors: {},
      hasUnsavedChanges: false,
      loading: false,
      saveConfigError: "",
    });
  }, [initialConfig]);

  return {
    state,
    actions: {
      openModal,
      closeModal,
      saveVirtualBox,
      deleteVirtualBox,
      updateMaxUnitsPerParcel,
      updateMaxWeightPerParcel,
      updateMaxVolumePerParcel,
      getPackageConfiguration,
      saveConfiguration,
      resetConfiguration,
      validatePackagingConfiguration,
      toggleVirtualBox
    },
  };
};
